// Range statistics plumbing for the All Transactions page.
//
// Apal (2026-09-22): "think of this page as showing me statistics of whatever
// range I select — the same as the signals page." The card math itself lives in
// signalAnalysisCalcs (single source of truth); this module only prepares the
// inputs from the all-transactions payload:
//
//   buildPairs     — entry/exit rows -> trade pairs, from the FULL history.
//                    Building from date-filtered rows was the fake-open bug: a
//                    trade exiting after the window lost its exit row and
//                    rendered as an open position.
//   pairsInWindow  — a trade belongs to the window iff its ENTRY date is inside
//                    (inclusive) — the same attribution Signal Analysis uses, so
//                    the two pages agree on any custom range.
//   countedTrades  — closed pairs with a gain, plus the genuinely open trade
//                    valued at its unrealized gain (Signal's include-open rule).
//   sliceSeries    — [date, close] history clipped to the window for the INDEX
//                    buy-&-hold side; seriesWindow resolves open-ended bounds.

const ENTRY_TYPES = new Set(['BUY', 'SHORT SELL']);
const EXIT_TYPES = new Set(['SELL', 'SELL (Forced)', 'SHORT COVER', 'SHORT COVER (Forced)']);

export const buildPairs = (transactions, openTrade) => {
  const pairs = [];
  const sorted = [...(transactions || [])].sort((a, b) => a.trade_number - b.trade_number);
  for (const cur of sorted) {
    if (!ENTRY_TYPES.has(cur.type)) continue;
    const isShort = cur.type === 'SHORT SELL';
    const exit = sorted.find(t => t.trade_number === cur.trade_number && EXIT_TYPES.has(t.type));
    let daysHeld = exit?.days_held ?? null;
    if (!exit && cur.date && openTrade?.last_close_date) {
      daysHeld = Math.floor((new Date(openTrade.last_close_date) - new Date(cur.date)) / 86400000);
    }
    pairs.push({
      trade_number: cur.trade_number,
      direction: isShort ? 'SHORT' : 'LONG',
      model_revision: cur.model_revision,
      entry_date: cur.date,
      entry_price: cur.price,
      exit_date: exit?.date,
      exit_price: exit?.price,
      exit_type: exit?.type,
      gain: exit?.gain ?? null,
      cumulative_gain: exit?.cumulative_gain ?? cur.cumulative_gain,
      days_held: daysHeld,
      mae: exit?.mae ?? (!exit && openTrade ? openTrade.mae : null),
      is_open: !exit,
      last_close: !exit ? openTrade?.last_close ?? null : null,
      last_close_date: !exit ? openTrade?.last_close_date ?? null : null,
    });
  }
  return pairs;
};

export const pairsInWindow = (pairs, from, to) =>
  (pairs || []).filter(p => {
    const d = p.entry_date || '';
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });

export const countedTrades = (pairs, openTrade) => {
  const comp = [];
  let openIncluded = false;
  for (const p of pairs || []) {
    if (p.is_open) {
      if (openTrade && openTrade.current_gain != null) {
        comp.push({ ...p, gain: openTrade.current_gain, mae: openTrade.mae ?? p.mae });
        openIncluded = true;
      }
    } else if (p.gain != null) {
      comp.push(p);
    }
  }
  return { comp, openIncluded };
};

export const sliceSeries = (series, from, to) =>
  (series || [])
    .filter(([d]) => (!from || d >= from) && (!to || d <= to))
    .map(([date, close]) => ({ date, close }));

// The last close BEFORE `from` — the basis an index return over the window
// starts from (null when the data begins inside the window).
export const basisBefore = (series, from) => {
  if (!from || !series) return null;
  let last = null;
  for (const [d, c] of series) { if (d < from) last = { date: d, close: c }; else break; }
  return last;
};

export const seriesWindow = (series, from, to) => {
  if (!series || !series.length) return { from: from || null, to: to || null };
  return { from: from || series[0][0], to: to || series[series.length - 1][0] };
};
